import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, MicVocal, Radio } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { WordSyncDisplay } from "@/components/WordSyncDisplay";

export default function KaraokeLive() {
  const { liveId } = useParams();
  const [notFound, setNotFound] = useState(false);
  const [sess, setSess] = useState(null);
  const [posMs, setPosMs] = useState(0);
  const playingRef = useRef(false);
  const trackIdRef = useRef(null);

  const fontSize = Math.max(26, Math.min(54, Math.floor(window.innerWidth / 13)));

  const loadFull = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/public/karaoke-live/${liveId}`);
      setSess(data);
      trackIdRef.current = data.track?.track_id || null;
      playingRef.current = !!data.playing;
      setPosMs(data.position_ms || 0);
    } catch {
      setNotFound(true);
    }
  }, [liveId]);

  useEffect(() => { loadFull(); }, [loadFull]);

  // follow the host: poll every 2s, re-fetch full payload when the track changes
  useEffect(() => {
    if (notFound) return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/karaoke-live/${liveId}/sync`);
        if ((data.track_id || null) !== trackIdRef.current) { await loadFull(); return; }
        playingRef.current = !!data.playing;
        setPosMs(data.position_ms || 0);
        setSess((s) => (s ? { ...s, active: data.active, playing: data.playing } : s));
      } catch { /* keep last state */ }
    }, 2000);
    return () => clearInterval(t);
  }, [liveId, notFound, loadFull]);

  // local clock between polls
  useEffect(() => {
    let raf, last = performance.now();
    const step = (t) => {
      if (playingRef.current) setPosMs((ms) => ms + (t - last));
      last = t;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (notFound) {
    return (
      <Shell>
        <div className="text-center px-6" data-testid="karaoke-live-notfound">
          <MicVocal className="h-10 w-10 mx-auto text-white/20 mb-4" />
          <div className="font-display text-2xl font-bold text-white mb-2">No karaoke night here</div>
          <div className="text-sm text-white/40">This link isn't live — double-check the QR code with the host.</div>
        </div>
      </Shell>
    );
  }

  if (!sess) {
    return (
      <Shell>
        <div className="text-white/40 text-sm" data-testid="karaoke-live-loading">
          <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Tuning in...
        </div>
      </Shell>
    );
  }

  const waiting = sess.active && !sess.playing && posMs < 200;

  return (
    <div className="min-h-screen flex flex-col bg-[#08080c]" data-testid="karaoke-live-page">
      <header className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <Radio className="h-4 w-4 text-[#f5a623] shrink-0" />
          <span className="font-display font-bold text-white truncate" data-testid="karaoke-live-station">{sess.station_name}</span>
        </div>
        {sess.active ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/50 bg-rose-500/10 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-rose-300" data-testid="karaoke-live-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-pulse" /> Live
          </span>
        ) : (
          <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-white/40" data-testid="karaoke-live-offline">Off air</span>
        )}
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-3 py-6">
        {!sess.active ? (
          <div className="text-center px-6" data-testid="karaoke-live-ended">
            <MicVocal className="h-10 w-10 mx-auto text-white/20 mb-4" />
            <div className="font-display text-2xl font-bold text-white mb-2">Karaoke night has wrapped</div>
            <div className="text-sm text-white/40">Thanks for singing along — keep this page open, the host may go live again.</div>
          </div>
        ) : sess.track ? (
          <>
            <div className="mb-6 text-center">
              <div className="font-display text-lg font-bold text-white" data-testid="karaoke-live-track">{sess.track.title}</div>
              {sess.track.artist && <div className="text-xs text-white/40 mt-0.5">{sess.track.artist}</div>}
            </div>
            <div className="relative w-full max-w-4xl flex items-center justify-center" style={{ minHeight: "45vh" }}>
              <WordSyncDisplay lines={sess.track.lines || []} currentMs={posMs} fontSize={fontSize} />
              {waiting && (
                <div className="absolute inset-x-0 -bottom-2 text-center text-xs font-mono uppercase tracking-widest text-white/30 animate-pulse" data-testid="karaoke-live-waiting">
                  Waiting for the host to hit play...
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-white/40 text-sm" data-testid="karaoke-live-notrack">The host is picking a track...</div>
        )}
      </main>

      <footer className="px-5 py-4 text-center">
        <a href="/?utm_source=karaoke_live" data-testid="karaoke-live-footer"
          className="text-[11px] font-mono uppercase tracking-widest text-white/25 hover:text-[#f5a623] transition-colors">
          Powered by BroadcastFlow AI
        </a>
      </footer>
    </div>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-[#08080c]">{children}</div>;
}
